import { SiweMessage } from 'siwe';
import { bsc } from 'viem/chains';

/**
 * Generate a cryptographically secure random nonce (16 bytes → 32 hex chars).
 * Use this for transaction-signing SIWE messages where you don't fetch the
 * nonce from the server. For authentication flows, always use getNonce() instead.
 */
export const generateSecureNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Get the appropriate chain ID based on environment
 * - Development: 31337 (Hardhat local chain)
 * - Production: BSC mainnet
 */
export const getChainId = (): number => {
  return import.meta.env.MODE === 'development' ? 31337 : bsc.id;
};

const getSiweDomain = () => {
  return window.location.host;
};

const getSiweUri = () => {
  return window.location.origin;
};

/**
 * Create a SIWE message with proper configuration
 * Automatically uses the correct chain ID based on environment
 */
export const createSiweMessage = (params: {
  address: string;
  statement: string;
  nonce: string; // required — always fetch from backend via getNonce()
}): SiweMessage => {
  if (!params.nonce) {
    throw new Error('SIWE nonce must be provided from the server');
  }
  return new SiweMessage({
    domain: getSiweDomain(),
    address: params.address,
    statement: params.statement,
    uri: getSiweUri(),
    version: '1',
    chainId: getChainId(),
    nonce: params.nonce,
  });
};
